/**
 * My Dashboard — the Care Worker landing page.
 *
 * Deliberately shows ONLY personal + site-scoped information so no
 * organisation-wide management data ever appears for frontline staff. All
 * data comes from /api/my-dashboard/summary which enforces the same scoping
 * on the backend.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarClock, Umbrella, GraduationCap, ClipboardList, MapPin, Bell,
  ChevronRight, AlertTriangle, Clock, Stethoscope, Home, Pill, LogIn, CheckCircle2,
  MessageSquarePlus, Send, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(11, 16); }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

function Section({ testid, icon: Icon, title, right, children }) {
  return (
    <section className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {title}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function MyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  // Priority Actions assigned to this Care Worker. Uses the existing
  // /readiness-actions/my endpoint — no new persistence.
  const [myActions, setMyActions] = useState({ items: [], open_count: 0, overdue_count: 0 });

  const load = () => {
    setLoading(true);
    return Promise.all([
      api.get("/my-dashboard/summary").then((r) => setData(r.data)),
      api.get("/readiness-actions/my")
        .then((r) => setMyActions(r.data || { items: [], open_count: 0, overdue_count: 0 }))
        .catch(() => setMyActions({ items: [], open_count: 0, overdue_count: 0 })),
    ])
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load your dashboard"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Fire-and-forget read receipts for every handover item we render — the
  // outgoing shift's manager view uses these to see that the handover landed.
  useEffect(() => {
    const items = data?.handover || [];
    if (!items.length) return;
    items.forEach((h) => {
      if (!h.source_id && !h.id) return;
      api.post("/my-dashboard/handover/read", { kind: h.kind, source_id: h.source_id || h.id }).catch(() => {});
    });
  }, [data]);

  const clockIn = async () => {
    if (!data?.shift_today?.id) return;
    setClocking(true);
    try {
      await api.post(`/visits/${data.shift_today.id}/check-in`);
      toast.success("Clocked in — have a great shift");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clock in"); }
    finally { setClocking(false); }
  };
  const clockOut = async () => {
    if (!data?.shift_today?.id) return;
    setClocking(true);
    try {
      await api.post(`/visits/${data.shift_today.id}/check-out`);
      toast.success("Clocked out — thanks for your work today");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clock out"); }
    finally { setClocking(false); }
  };

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || user?.email || "").split(" ")[0] || (user?.email || "").split("@")[0];

  // Deep-link resolver for a "My Actions" row so tapping it takes the staff
  // member straight to the record that needs attention.
  const actionHref = (a) => {
    if (a.kind === "training") return "/records/training";
    if (a.kind === "leave-info") return "/time-off-requests";
    return "/time-off-requests";
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="my-dashboard">
      {/* Desktop page header — hidden on mobile/tablet where MobilePageHeader owns it */}
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Your Workspace</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="my-dashboard-title">My Dashboard</h1>
        </div>
      </div>

      <div className="p-4 lg:p-6 space-y-4 lg:space-y-6 pb-24 lg:pb-6">
        <div>
          <div className="text-2xl font-heading font-semibold text-slate-900" data-testid="my-greeting">{greeting}, {firstName}</div>
          <div className="text-sm text-slate-600 mt-1">Here's what you need for your shift today.</div>
        </div>

        {/* Prominent Clock-in / Clock-out CTA — only when there is a shift
            scheduled for today. Colour signals current state (blue: not yet
            clocked in, emerald: on shift, slate: completed). */}
        {data?.shift_today && !data.shift_today.checked_out_at && (
          <div
            className={`p-4 border flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 ${data.shift_today.checked_in_at ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-sky-50 border-sky-300 text-sky-900"}`}
            data-testid="clock-in-cta"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
              {data.shift_today.checked_in_at ? <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" /> : <LogIn className="w-6 h-6 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">
                  {data.shift_today.checked_in_at ? "You are on shift" : "Clock in for today's shift"}
                </div>
                <div className="text-xs opacity-90 mt-0.5 break-words">
                  {data.shift_today.checked_in_at
                    ? `Clocked in at ${fmtTime(data.shift_today.checked_in_at)} · ends ${fmtTime(data.shift_today.scheduled_end)}`
                    : `${fmtTime(data.shift_today.scheduled_start)} → ${fmtTime(data.shift_today.scheduled_end)} · ${data.shift_today.site_name || ""}`}
                </div>
              </div>
            </div>
            {data.shift_today.checked_in_at ? (
              <Button onClick={clockOut} disabled={clocking} data-testid="btn-clock-out" className="bg-emerald-700 hover:bg-emerald-800 text-white h-11 w-full sm:w-auto">
                {clocking ? "Clocking out…" : "Clock out"}
              </Button>
            ) : (
              <Button onClick={clockIn} disabled={clocking} data-testid="btn-clock-in" className="bg-sky-700 hover:bg-sky-800 text-white h-11 w-full sm:w-auto">
                {clocking ? "Clocking in…" : "Clock in"}
              </Button>
            )}
          </div>
        )}

      {/* Mobile quick actions — visible <md; supplements the bottom nav for
          one-tap access to the most common flows. Hidden on desktop where the
          full page + sidebar is available. */}
      <div className="md:hidden grid grid-cols-2 gap-3 mb-4" data-testid="my-quick-actions-mobile">
        <QuickAction to="/my-shifts" icon={CalendarClock} label="My Shifts" testid="qa-mobile-my-shifts" />
        <QuickAction to="/mar-chart" icon={Pill} label="MAR Chart" testid="qa-mobile-mar-chart" />
        <QuickAction to="/time-off-requests" icon={Umbrella} label="Request Leave" testid="qa-mobile-request-leave" />
        <QuickAction to="/records/daily-notes" icon={ClipboardList} label="Daily Notes" testid="qa-mobile-daily-notes" />
      </div>

        {/* Quick actions — desktop/tablet */}
        <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="my-quick-actions">
          <QuickAction to="/my-shifts" icon={CalendarClock} label="My Shifts" testid="qa-my-shifts" />
          <QuickAction to="/mar-chart" icon={Pill} label="MAR Chart" testid="qa-mar-chart" />
          <QuickAction to="/time-off-requests" icon={Umbrella} label="Request Leave" testid="qa-request-leave" />
          <QuickAction to="/records/daily-notes" icon={ClipboardList} label="Daily Notes" testid="qa-daily-notes" />
        </div>

        {loading ? (
          <div className="text-sm text-slate-500" data-testid="my-loading">Loading your dashboard…</div>
        ) : !data ? (
          <div className="text-sm text-slate-500">Your dashboard is unavailable right now — please retry in a moment.</div>
        ) : (
          <>
            {/* Row 1: Shift today + next shift + holiday */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Section testid="section-shift-today" icon={Clock} title="My shift today"
                right={<Link to="/my-shifts" className="text-[11px] text-sky-700 hover:underline inline-flex items-center gap-0.5" data-testid="link-my-shifts">Open <ChevronRight className="w-3 h-3" /></Link>}
              >
                {data.shift_today ? (
                  <div>
                    <div className="text-2xl font-heading font-semibold tabular-nums" data-testid="shift-today-time">
                      {fmtTime(data.shift_today.scheduled_start)} → {fmtTime(data.shift_today.scheduled_end)}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {data.shift_today.site_name || "—"}
                    </div>
                    {data.shift_today.client_name && (
                      <div className="text-xs text-slate-600 mt-0.5">With <b>{data.shift_today.client_name}</b></div>
                    )}
                    <div className="mt-2 text-[10px] uppercase tracking-widest">
                      <span className={`px-1.5 py-0.5 border ${data.shift_today.checked_in_at ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}>
                        {data.shift_today.checked_in_at ? (data.shift_today.checked_out_at ? "Complete" : "On shift") : (data.shift_today.status || "scheduled")}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500" data-testid="no-shift-today">No shift scheduled today.</div>
                )}
                {data.next_shift && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Next shift</div>
                    <div><b>{fmtDate(data.next_shift.scheduled_start)}</b> · {fmtTime(data.next_shift.scheduled_start)}</div>
                    <div className="text-slate-600">{data.next_shift.site_name || ""} {data.next_shift.client_name ? `· ${data.next_shift.client_name}` : ""}</div>
                  </div>
                )}
              </Section>

              <Section testid="section-my-actions" icon={ClipboardList} title="My actions">
                {data.actions.length === 0 ? (
                  <div className="text-sm text-slate-500">You're all caught up — nothing needs your attention.</div>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {data.actions.slice(0, 4).map((a, i) => (
                      <li key={i}>
                        <Link
                          to={actionHref(a)}
                          className="block border-l-2 border-amber-500 pl-3 py-1 hover:bg-slate-50 focus:bg-slate-100 rounded-r"
                          data-testid={`my-action-${i}`}
                        >
                          <div className="font-medium text-slate-900 flex items-center justify-between gap-2">
                            <span className="truncate">{a.title}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          </div>
                          <div className="text-xs text-slate-600">{a.detail}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {data.holiday && (
                <Section testid="section-holiday" icon={Umbrella} title="My holiday"
                  right={<Link to="/time-off-requests" className="text-[11px] text-sky-700 hover:underline inline-flex items-center gap-0.5" data-testid="link-request-leave">Request <ChevronRight className="w-3 h-3" /></Link>}
                >
                  <div className="text-2xl font-heading font-semibold tabular-nums" data-testid="my-holiday-available">
                    {fmt(data.holiday.available)} <span className="text-sm font-normal text-slate-500">{data.holiday.unit === "hours" ? "Hours" : "Days"} Available</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    <span>{fmt(data.holiday.taken)} Taken</span>
                    <span className="mx-1 text-slate-300">·</span>
                    <span>{fmt(data.holiday.booked)} Booked</span>
                    <span className="mx-1 text-slate-300">·</span>
                    <span className={data.holiday.pending > 0 ? "text-amber-700" : ""}>{fmt(data.holiday.pending)} Pending</span>
                  </div>
                </Section>
              )}
            </div>

            {/* Row 2: My training */}
            <Section testid="section-training" icon={GraduationCap} title="My training compliance">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                <Stat testid="training-compliance" label="Compliance" value={`${data.training.compliance_pct}%`} tone="ok" />
                <Stat testid="training-total" label="Certificates" value={data.training.total} />
                <Stat testid="training-expiring" label="Expiring soon" value={data.training.expiring_soon_count} tone={data.training.expiring_soon_count > 0 ? "warn" : "slate"} />
                <Stat testid="training-expired" label="Expired" value={data.training.expired_count} tone={data.training.expired_count > 0 ? "red" : "slate"} />
              </div>
              {(data.training.expired.length + data.training.expiring_soon.length) > 0 && (
                <ul className="text-xs space-y-1" data-testid="training-items">
                  {data.training.expired.map((t, i) => (
                    <li key={`e${i}`}>
                      <Link to="/records/training" className="flex flex-wrap items-center justify-between gap-1 border-l-2 border-red-500 pl-3 py-2 min-h-[44px] hover:bg-red-50" data-testid={`training-expired-${i}`}>
                        <span className="break-words">{t.course || t.training_type || t.name || "Course"}</span>
                        <span className="text-red-700 font-semibold shrink-0">Expired {t.expiry_date}</span>
                      </Link>
                    </li>
                  ))}
                  {data.training.expiring_soon.map((t, i) => (
                    <li key={`s${i}`}>
                      <Link to="/records/training" className="flex flex-wrap items-center justify-between gap-1 border-l-2 border-amber-500 pl-3 py-2 min-h-[44px] hover:bg-amber-50" data-testid={`training-expiring-${i}`}>
                        <span className="break-words">{t.course || t.training_type || t.name || "Course"}</span>
                        <span className="text-amber-700 font-semibold shrink-0">Due {t.expiry_date}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Row 3: Handover + upcoming appointments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section testid="section-handover" icon={Home} title="Shift handover · last 24h"
                right={<HandoverComposeButton onPosted={load} />}
              >
                {data.handover.length === 0 ? (
                  <div className="text-sm text-slate-500">Nothing to hand over on your site.</div>
                ) : (
                  <ul className="text-sm space-y-2">
                    {data.handover.map((h, i) => (
                      <li key={i} className={`border-l-2 pl-3 ${h.kind === "shift-handover" ? "border-sky-500 bg-sky-50/40 py-1" : "border-slate-300"}`} data-testid={`handover-${i}`}>
                        <div className="font-medium text-slate-900">{h.title}</div>
                        {h.detail && <div className="text-xs text-slate-600 line-clamp-2">{h.detail}</div>}
                        <div className="text-[11px] text-slate-500">{h.at ? new Date(h.at).toLocaleString() : ""}{h.by ? ` · ${h.by}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                )}
                {data.handover.length > 0 && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1" data-testid="handover-read-receipt">
                    <CheckCircle2 className="w-3 h-3" /> Handover marked as read
                  </div>
                )}
              </Section>

              <Section testid="section-appointments" icon={Stethoscope} title="Upcoming appointments">
                {data.upcoming_appointments.length === 0 ? (
                  <div className="text-sm text-slate-500">No upcoming appointments in the next 14 days.</div>
                ) : (
                  <ul className="text-sm space-y-2">
                    {data.upcoming_appointments.map((a) => (
                      <li key={a.id} className="border-l-2 border-sky-400 pl-3" data-testid={`appt-${a.id}`}>
                        <div className="font-medium text-slate-900">{a.title || a.type || "Appointment"}</div>
                        <div className="text-xs text-slate-600">{a.client_name} · {fmtDate(a.scheduled_at)} {fmtTime(a.scheduled_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>

            {/* Row 4: notifications */}
            <Section testid="section-notifications" icon={Bell} title="My notifications">
              {data.notifications.length === 0 ? (
                <div className="text-sm text-slate-500">No new notifications.</div>
              ) : (
                <ul className="text-sm space-y-2">
                  {data.notifications.map((n, i) => (
                    <li key={i} className="flex items-start gap-2 border-l-2 border-slate-200 pl-3" data-testid={`notif-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{n.title}</div>
                        {n.detail && <div className="text-xs text-slate-600">{n.detail}</div>}
                        <div className="text-[11px] text-slate-500">{n.at ? new Date(n.at).toLocaleString() : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Row 5: Priority actions assigned to this Care Worker.
                Reuses the existing /readiness-actions/my endpoint — no new
                permissions, no access to the Manager CQC dashboard. Care
                Workers can move through Not Started → In Progress → Completed
                only; governance fields (owner, priority, due date) stay
                Manager/Admin-only. */}
            {myActions.items.length > 0 && (
              <Section testid="section-my-priority-actions" icon={ShieldCheck} title="My priority actions">
                <div className="text-[11px] text-slate-500 mb-2" data-testid="my-priority-count">
                  <b>{myActions.open_count}</b> open
                  {myActions.overdue_count > 0 && (
                    <> · <span className="text-red-700 font-semibold">{myActions.overdue_count} overdue</span></>
                  )}
                </div>
                <ul className="divide-y divide-slate-100 text-sm" data-testid="my-priority-list">
                  {myActions.items.map((a) => {
                    const tone = a.status === "overdue" ? "border-l-red-500 bg-red-50"
                      : a.status === "completed" ? "border-l-emerald-500 bg-emerald-50/40"
                      : a.status === "in_progress" ? "border-l-sky-500 bg-sky-50/40"
                      : "border-l-slate-300";
                    const priorityTone = a.priority === "high"
                      ? "bg-red-50 text-red-800 border-red-300"
                      : "bg-amber-50 text-amber-800 border-amber-300";
                    return (
                      <li key={a.action_key} className={`py-2 pl-3 pr-1 border-l-2 ${tone}`}
                          data-testid={`my-priority-${a.action_key}`}>
                        <div className="flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-2">
                          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${priorityTone}`}
                                data-testid={`my-priority-tone-${a.action_key}`}>
                            {a.priority || "medium"}
                          </span>
                          <div className="flex-1 min-w-0 w-full sm:w-auto">
                            <div className="text-sm font-medium text-slate-900 break-words">{a.action_text}</div>
                            <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                              <span>{a.area}</span>
                              <span>·</span>
                              <span className={a.status === "overdue" ? "text-red-700 font-semibold" : ""}>
                                {a.status === "overdue" ? "OVERDUE" : (a.due_date ? `due ${a.due_date}` : "no due date")}
                              </span>
                            </div>
                          </div>
                          <select
                            value={["not_started", "in_progress", "completed"].includes(a.status) ? a.status : "in_progress"}
                            onChange={async (e) => {
                              try {
                                await api.patch(`/readiness-actions/${a.action_key}/status`, { status: e.target.value });
                                const r = await api.get("/readiness-actions/my");
                                setMyActions(r.data);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not update status");
                              }
                            }}
                            className="text-[12px] h-11 sm:h-8 w-full sm:w-auto rounded border border-slate-300 bg-white px-2"
                            data-testid={`my-priority-status-${a.action_key}`}
                          >
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="text-[10px] text-slate-400 mt-2 italic">
                  Managers assign these actions and decide priority, owner and due dates. Update your status as you make progress.
                </div>
              </Section>
            )}
          </>
        )}
      </div>

      {/* Mobile bottom nav — visible only on phones; hidden ≥md. Turns the
          Care Worker's dashboard into a shift-side home screen. */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.04)] flex" data-testid="my-mobile-nav">
        <MobileTab to="/" icon={Home} label="Home" testid="mnav-home" />
        <MobileTab to="/my-shifts" icon={CalendarClock} label="Shifts" testid="mnav-shifts" />
        <MobileTab to="/mar-chart" icon={Pill} label="MAR" testid="mnav-mar" />
        <MobileTab to="/time-off-requests" icon={Umbrella} label="Leave" testid="mnav-leave" />
        <MobileTab to="/records/daily-notes" icon={ClipboardList} label="Notes" testid="mnav-notes" />
      </nav>
    </div>
  );
}

function MobileTab({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex-1 flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-widest text-slate-600 hover:bg-slate-50 active:bg-slate-100"
    >
      <Icon className="w-5 h-5 mb-0.5" strokeWidth={1.75} />
      {label}
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label, testid }) {
  return (
    <Button asChild variant="outline" className="h-16 justify-start bg-white hover:bg-slate-50" data-testid={testid}>
      <Link to={to} className="flex items-center gap-3">
        <div className="w-9 h-9 border border-slate-200 flex items-center justify-center text-slate-700">
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
      </Link>
    </Button>
  );
}

function HandoverComposeButton({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [highlights, setHighlights] = useState("");
  const [concerns, setConcerns] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!note.trim()) {
      toast.error("Please add a handover note before posting");
      return;
    }
    setBusy(true);
    try {
      await api.post("/my-dashboard/handover/compose", {
        note: note.trim(),
        highlights: highlights.trim() || null,
        concerns: concerns.trim() || null,
      });
      toast.success("Handover posted — the next shift will see it");
      setNote(""); setHighlights(""); setConcerns("");
      setOpen(false);
      onPosted && onPosted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post handover");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200"
        data-testid="btn-open-compose-handover"
      >
        <MessageSquarePlus className="w-3.5 h-3.5" /> Post handover
      </button>
    );
  }

  return (
    <div className="w-full mt-2" data-testid="compose-handover-panel">
      <div className="border border-sky-200 bg-sky-50/40 rounded-md p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-sky-800 font-semibold">Post end-of-shift handover</div>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What does the next shift need to know? (required)"
          className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
          data-testid="compose-handover-note"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            value={highlights}
            onChange={(e) => setHighlights(e.target.value)}
            placeholder="Positives / highlights (optional)"
            className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-1.5"
            data-testid="compose-handover-highlights"
          />
          <input
            value={concerns}
            onChange={(e) => setConcerns(e.target.value)}
            placeholder="Concerns for next shift (optional)"
            className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-1.5"
            data-testid="compose-handover-concerns"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-end">
          <button
            onClick={() => setOpen(false)}
            className="h-11 sm:h-8 px-3 rounded-md text-[13px] font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 sm:border-0 w-full sm:w-auto"
            data-testid="compose-handover-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !note.trim()}
            className="inline-flex items-center justify-center gap-1.5 h-11 sm:h-8 px-3 rounded-md text-[13px] font-semibold text-white bg-sky-700 hover:bg-sky-800 disabled:opacity-50 w-full sm:w-auto"
            data-testid="compose-handover-submit"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Posting…" : "Post handover"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {  const toneClass = {
    slate: "bg-white border-slate-200",
    ok: "bg-emerald-50 border-emerald-300",
    warn: "bg-amber-50 border-amber-300",
    red: "bg-red-50 border-red-300",
  }[tone];
  return (
    <div className={`${toneClass} border p-3`} data-testid={testid}>
      <div className="text-2xl font-heading font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">{label}</div>
    </div>
  );
}
